import { useState, useEffect, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import { sendNotificationEmail } from '../../utils/sendNotificationEmail';
import AdminSupportMessages from './AdminSupportMessages';
import AdminSettings from './AdminSettings';
import LanguageToggle from '../../components/LanguageToggle';
import { useLanguagePreference } from '../../i18n/useLanguagePreference';
import StatCard from '../../components/StatCard';
import LegalLinksRow from '../../components/LegalLinksRow';
import VocationWorksLogo from '../../components/VocationWorksLogo';

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const GREEN    = '#1D9E75';
const BLUE     = '#60A5FA';
const PURPLE   = '#A78BFA';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const TABS = ['Workers', 'Employers', 'Unions', 'Jobs', 'Applications', 'Certifications', 'Waitlist', 'Organizations', 'Support', 'Settings'];

const TAB_LABEL_KEYS = {
  'Workers': 'nav.admin.workers',
  'Employers': 'nav.admin.employers',
  'Unions': 'nav.admin.unions',
  'Jobs': 'nav.admin.jobs',
  'Applications': 'nav.admin.applications',
  'Certifications': 'nav.admin.certifications',
  'Waitlist': 'nav.admin.waitlist',
  'Organizations': 'nav.admin.organizations',
  'Support': 'nav.admin.support',
  'Settings': 'nav.admin.settings',
};

function IconUsers({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconBuilding({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 21V9" />
    </svg>
  );
}
function IconBriefcase({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  );
}
function IconCheck({ color }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="8 12 11 15 16 9" />
    </svg>
  );
}

function SearchBar({ value, onChange, placeholder, clearAriaLabel }) {
  return (
    <div style={{ padding: '14px 20px', backgroundColor: '#0b1621', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <div style={{ position: 'relative', maxWidth: '380px' }}>
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="rgba(255,255,255,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', flexShrink: 0 }}
        >
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '9px 32px 9px 32px',
            fontSize: '12px', fontFamily: FONT_BODY,
            background: NAVY_MID, border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px', color: WHITE, outline: 'none',
          }}
        />
        {value && (
          <button
            onClick={() => onChange('')}
            style={{
              position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
              color: 'rgba(255,255,255,0.4)', fontSize: '14px', lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label={clearAriaLabel}
          >✕</button>
        )}
      </div>
    </div>
  );
}

const inp = {
  width: '100%', padding: '9px 12px', fontSize: '0.88rem',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
  fontFamily: FONT_BODY,
};
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' };
const fld = { marginBottom: '0.85rem' };

function InviteStatusBadge({ status, t }) {
  const cfg = {
    accepted: { bg: 'rgba(29,158,117,0.15)', color: '#1D9E75', label: t('admin.dashboard.organizations.inviteStatusAccepted') },
    expired:  { bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', label: t('admin.dashboard.organizations.inviteStatusExpired') },
  }[status] || { bg: 'rgba(245,166,35,0.15)', color: AMBER, label: t('admin.dashboard.organizations.inviteStatusPending') };
  return (
    <span style={{ backgroundColor: cfg.bg, color: cfg.color, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

const th = {
  padding: '10px 16px', textAlign: 'left',
  fontSize: '0.68rem', fontWeight: 700,
  color: 'rgba(255,255,255,0.38)', letterSpacing: '0.1em', textTransform: 'uppercase',
  borderBottom: '1px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap',
  backgroundColor: '#0b1621',
};
const td = {
  padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)',
  color: WHITE, fontSize: '0.875rem', verticalAlign: 'middle', whiteSpace: 'nowrap',
};
const tdMuted = { ...td, color: 'rgba(255,255,255,0.42)', fontSize: '0.82rem' };

function DeleteBtn({ onClick, t }) {
  return (
    <button
      onClick={onClick}
      style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', fontSize: '0.75rem', fontWeight: 600, padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY, whiteSpace: 'nowrap' }}
    >
      {t('messaging.delete')}
    </button>
  );
}

function DemoToggleBtn({ isDemo, onClick, t }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: isDemo ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${isDemo ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.15)'}`,
        color: isDemo ? '#60A5FA' : 'rgba(255,255,255,0.45)',
        fontSize: '0.75rem', fontWeight: 700, padding: '5px 12px', borderRadius: '6px',
        cursor: 'pointer', fontFamily: FONT_BODY, whiteSpace: 'nowrap',
      }}
    >
      {isDemo ? t('admin.dashboard.shared.unmarkDemoButton') : t('admin.dashboard.shared.markDemoButton')}
    </button>
  );
}

function CertStatusBadge({ status, t }) {
  const cfg = {
    verified:   { bg: 'rgba(29,158,117,0.15)',   color: '#1D9E75',             label: t('admin.dashboard.certifications.statusVerified') },
    pending:    { bg: 'rgba(245,166,35,0.15)',    color: '#F5A623',             label: t('admin.dashboard.certifications.filterPendingReview') },
    rejected:   { bg: 'rgba(248,113,113,0.12)',   color: '#F87171',             label: t('admin.dashboard.certifications.filterRejected') },
    unverified: { bg: 'rgba(255,255,255,0.08)',   color: 'rgba(255,255,255,0.4)', label: t('admin.dashboard.certifications.statusUnverified') },
  }[status] || { bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', label: t('admin.dashboard.certifications.statusUnverified') };
  return (
    <span style={{ backgroundColor: cfg.bg, color: cfg.color, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

function StatusBadge({ status, t }) {
  const cfg = {
    active:     { bg: 'rgba(29,158,117,0.15)',  color: GREEN,     label: t('admin.dashboard.shared.statusActive') },
    filled:     { bg: 'rgba(59,130,246,0.15)',  color: '#60A5FA', label: t('admin.dashboard.shared.statusFilled') },
    reviewing:  { bg: 'rgba(59,130,246,0.15)',  color: '#60A5FA', label: t('admin.dashboard.shared.statusReviewing') },
    rejected:   { bg: 'rgba(248,113,113,0.12)', color: '#F87171', label: t('admin.dashboard.shared.statusRejected') },
    interested: { bg: 'rgba(245,166,35,0.15)',  color: AMBER,     label: t('admin.dashboard.shared.statusInterested') },
    deactivated:{ bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)', label: t('admin.dashboard.shared.statusDeactivated') },
  }[status] || { bg: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)', label: status || '—' };

  return (
    <span style={{ backgroundColor: cfg.bg, color: cfg.color, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

function EmptyState({ text }) {
  return (
    <div style={{ padding: '48px 24px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.9rem' }}>
      {text}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tabLabel = (tabName) => TAB_LABEL_KEYS[tabName] ? t(TAB_LABEL_KEYS[tabName]) : tabName;
  const [refreshKey, setRefreshKey]     = useState(0);
  const [tab, setTab]                   = useState('Workers');
  const [loading, setLoading]           = useState(true);
  const [workers, setWorkers]           = useState([]);
  const [employers, setEmployers]       = useState([]);
  const [unions, setUnions]             = useState([]);
  const [jobs, setJobs]                 = useState([]);
  const [applications, setApplications] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [certFilter, setCertFilter]         = useState('all');
  const [waitlist, setWaitlist]             = useState([]);
  const [orgInvites, setOrgInvites]         = useState([]);
  const [organizations, setOrganizations]   = useState([]);
  const [isMobile, setIsMobile]             = useState(window.innerWidth < 768);
  const [adminUser, setAdminUser]           = useState(null);
  useLanguagePreference(adminUser?.id);
  const [authorized, setAuthorized]         = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  // Per-employer add-on editing (extra_users / extra_jobs) -- an expandable panel per row
  // rather than inline table columns, since two more inputs plus their computed-effect text
  // wouldn't fit the existing table's mobile width alongside company/contact/email/location.
  const [expandedAddonsId, setExpandedAddonsId] = useState(null);
  // Organizations tab: per-org staff roster + admin reassignment, keyed by org id so each
  // expanded row keeps its own selection//status without a shared global form.
  const [expandedOrgStaffId, setExpandedOrgStaffId] = useState(null);
  const [orgStaffRows, setOrgStaffRows]       = useState({});
  const [orgStaffLoading, setOrgStaffLoading] = useState({});
  const [orgReassignPick, setOrgReassignPick] = useState({});
  const [orgReassignSaving, setOrgReassignSaving] = useState({});
  const [orgReassignStatus, setOrgReassignStatus] = useState({});
  const [addonsEdits, setAddonsEdits]     = useState({}); // employerId -> { extra_jobs, extra_users }
  const [addonsLimits, setAddonsLimits]   = useState({}); // employerId -> { job_limit, user_limit }
  const [addonsSaving, setAddonsSaving]   = useState({}); // employerId -> bool
  const [addonsStatus, setAddonsStatus]   = useState({}); // employerId -> { ok, msg }
  const tableScrollRef = useRef(null);

  // Live search, one per tab
  const [workerSearch, setWorkerSearch]           = useState('');
  const [employerSearch, setEmployerSearch]       = useState('');
  const [unionSearch, setUnionSearch]             = useState('');
  const [jobSearch, setJobSearch]                 = useState('');
  const [applicationSearch, setApplicationSearch] = useState('');
  const [certSearch, setCertSearch]               = useState('');
  const [waitlistSearch, setWaitlistSearch]       = useState('');
  const [orgSearch, setOrgSearch]                 = useState('');

  const [foundingForm, setFoundingForm]           = useState({ name: '', email: '', notes: '' });
  const [foundingSubmitting, setFoundingSubmitting] = useState(false);
  const [foundingMsg, setFoundingMsg]             = useState('');
  const [foundingMsgIsSuccess, setFoundingMsgIsSuccess] = useState(false);

  // Real admin check, matching the defense-in-depth pattern already used on the Employer/Union/
  // Organization dashboards: ProtectedRoute (in App.js) only confirms a session exists, so this
  // effect additionally confirms the session actually belongs to an admin before rendering anything.
  useEffect(() => {
    const checkAdmin = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/admin'); return; }

      const { data: isAdmin, error: isAdminError } = await supabase.rpc('is_admin');
      if (isAdminError || !isAdmin) { navigate('/admin'); return; }

      setAdminUser(session.user);
      setAuthorized(true);
    };
    checkAdmin();
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${NAVY}; font-family: 'DM Sans', sans-serif; }
      .admin-table tr:hover td { background: rgba(255,255,255,0.025); }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(link); document.head.removeChild(style); };
  }, []);

  useEffect(() => {
    if (!authorized) return;
    const fetchAll = async () => {
      const [workersRes, tradesRes, employersRes, jobsRes, appsRes, certsRes, waitlistRes, orgInvitesRes, orgsRes, orgStaffRes, orgMembersRes, addonsRes, unionsRes, apprRes] = await Promise.all([
        supabase.from('workers').select('*').order('created_at', { ascending: false }),
        supabase.from('worker_trades').select('worker_id, trade'),
        supabase.from('employers').select('*').order('created_at', { ascending: false }),
        supabase.from('jobs').select('*').order('created_at', { ascending: false }),
        supabase.from('applications').select('*').order('created_at', { ascending: false }),
        supabase.from('worker_certifications').select('*').order('created_at', { ascending: false }),
        supabase.from('waitlist').select('*').order('created_at', { ascending: false }),
        supabase.from('organization_invites').select('*').eq('invite_type', 'founding').order('created_at', { ascending: false }),
        supabase.from('organizations').select('*').order('created_at', { ascending: false }),
        supabase.from('organization_staff').select('organization_id'),
        supabase.from('organization_members').select('organization_id'),
        supabase.from('employer_addons').select('*'),
        supabase.from('union_accounts').select('*').order('created_at', { ascending: false }),
        supabase.from('apprenticeships').select('id, union_id, status'),
      ]);

      const tradeMap = Object.fromEntries((tradesRes.data || []).map(t => [t.worker_id, t.trade]));
      setWorkers((workersRes.data || []).map(w => ({ ...w, trade: tradeMap[w.id] || '—' })));

      const jobsByEmployer = (jobsRes.data || []).reduce((acc, j) => {
        acc[j.employer_id] = (acc[j.employer_id] || 0) + 1;
        return acc;
      }, {});
      const addonsByEmployer = Object.fromEntries((addonsRes.data || []).map(a => [a.employer_id, a]));
      setEmployers((employersRes.data || []).map(e => ({
        ...e,
        jobsPosted: jobsByEmployer[e.id] || 0,
        extraJobs:  addonsByEmployer[e.id]?.extra_jobs  || 0,
        extraUsers: addonsByEmployer[e.id]?.extra_users || 0,
      })));

      // Postings count = active apprenticeship postings, the union-side equivalent of an
      // employer's jobs -- counted client-side from one fetch, same as jobsByEmployer above.
      const apprByUnion = (apprRes.data || []).reduce((acc, a) => {
        if (a.status === 'active') acc[a.union_id] = (acc[a.union_id] || 0) + 1;
        return acc;
      }, {});
      setUnions((unionsRes.data || []).map(u => ({ ...u, postings: apprByUnion[u.id] || 0 })));

      const rawJobs = jobsRes.data || [];
      setJobs(rawJobs);

      const workerMap = Object.fromEntries((workersRes.data || []).map(w => [w.id, w]));
      const jobMap    = Object.fromEntries(rawJobs.map(j => [j.id, j]));
      setApplications((appsRes.data || []).map(app => {
        const w = workerMap[app.worker_id];
        const j = jobMap[app.job_id];
        return {
          ...app,
          workerName:  w ? `${w.first_name} ${w.last_name}` : '—',
          jobTitle:    j?.job_title    || '—',
          companyName: j?.company_name || '—',
        };
      }));

      setCertifications((certsRes.data || []).map(cert => {
        const w = workerMap[cert.worker_id];
        return {
          ...cert,
          workerName:  w ? `${w.first_name} ${w.last_name}` : '—',
          workerEmail: w?.email || null,
        };
      }));

      setWaitlist(waitlistRes.data || []);

      const foundingInvites = orgInvitesRes.data || [];
      const orgIds = [...new Set(foundingInvites.map(i => i.organization_id).filter(Boolean))];
      let orgNameMap = {};
      if (orgIds.length > 0) {
        const { data: orgsRes } = await supabase.from('organizations').select('id, org_name').in('id', orgIds);
        orgNameMap = Object.fromEntries((orgsRes || []).map(o => [o.id, o.org_name]));
      }
      setOrgInvites(foundingInvites.map(i => ({ ...i, orgName: i.organization_id ? (orgNameMap[i.organization_id] || '—') : null })));

      const staffCountByOrg = (orgStaffRes.data || []).reduce((acc, s) => { acc[s.organization_id] = (acc[s.organization_id] || 0) + 1; return acc; }, {});
      const memberCountByOrg = (orgMembersRes.data || []).reduce((acc, m) => { acc[m.organization_id] = (acc[m.organization_id] || 0) + 1; return acc; }, {});
      setOrganizations((orgsRes.data || []).map(o => ({
        ...o,
        staffCount: staffCountByOrg[o.id] || 0,
        memberCount: memberCountByOrg[o.id] || 0,
      })));

      setLoading(false);
    };
    fetchAll();
  }, [refreshKey, authorized]); // eslint-disable-line

  // Support badge: computed independently of AdminSupportMessages (which owns the same
  // count internally) because that component only mounts once the admin is already on
  // the Support tab -- without this, the nav badge would never update live while the
  // admin is looking at any other tab.
  useEffect(() => {
    if (!authorized) return;
    const loadSupportUnreadCount = async () => {
      const { count } = await supabase
        .from('support_messages')
        .select('id', { count: 'exact', head: true })
        .eq('sender_type', 'account')
        .is('read_at', null);
      setSupportUnreadCount(count || 0);
    };
    loadSupportUnreadCount();
    const channel = supabase
      .channel('admin-support-badge')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_messages' }, loadSupportUnreadCount)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [authorized]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  // Deleting the auth.users login itself requires the service-role key (there's no RLS-scoped
  // way to do it), so that one step has to go through a dedicated server endpoint -- called as
  // the final step after handleDeleteWorker/handleDeleteEmployer's RPC-based data cleanup below
  // succeeds.
  const deleteAccountLogin = async (id) => {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/admin-delete-account', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
      body: JSON.stringify({ userId: id }),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Unknown error');
  };

  const handleDeleteWorker = async (id) => {
    if (!window.confirm(t('admin.dashboard.workers.deleteConfirm'))) return;
    // The individual client-side table.delete() chain this used to be proved genuinely
    // unreliable in production -- several tables would silently match zero rows despite a
    // 204 response, for reasons that resisted extensive direct debugging. admin_delete_worker_data
    // is a SECURITY DEFINER RPC that verifies the caller is a real admin itself, then handles
    // the entire real cleanup cascade in one atomic, server-side call.
    const { error } = await supabase.rpc('admin_delete_worker_data', { p_worker_id: id });
    if (error) { alert(error.message); return; }
    setWorkers(prev => prev.filter(w => w.id !== id));
    try {
      await deleteAccountLogin(id);
    } catch (err) {
      alert(t('admin.dashboard.shared.deleteLoginFailedError', { error: err.message }));
    }
  };

  // Toggles is_demo for a worker/employer -- the flag the RLS layer uses to keep demo
  // accounts fully usable to their own dashboard while invisible/non-interactive with
  // real users elsewhere in the app (see is_demo_worker/is_demo_employer + the policies
  // on jobs, employers, applications, conversations, job_offers, interviews).
  const handleToggleDemo = async (table, id, current) => {
    // .select().single() (not a bare .update()) so an RLS policy silently matching zero rows
    // -- an errorless 0-row "success" -- surfaces as a thrown/null result instead of the UI
    // optimistically flipping to a state the database never actually reached.
    const { data, error } = await supabase.from(table).update({ is_demo: !current }).eq('id', id).select().single();
    if (error || !data) { alert(error?.message || 'Update did not take effect.'); return; }
    if (table === 'workers') {
      setWorkers(prev => prev.map(w => w.id === id ? { ...w, is_demo: data.is_demo } : w));
    } else if (table === 'union_accounts') {
      setUnions(prev => prev.map(u => u.id === id ? { ...u, is_demo: data.is_demo } : u));
    } else {
      setEmployers(prev => prev.map(e => e.id === id ? { ...e, is_demo: data.is_demo } : e));
    }
  };

  const handleDeleteEmployer = async (id) => {
    if (!window.confirm(t('admin.dashboard.employers.deleteConfirm'))) return;
    // Same reasoning as handleDeleteWorker above -- admin_delete_employer_data replaces the
    // old client-side table.delete() chain entirely, atomically, server-side.
    const { error } = await supabase.rpc('admin_delete_employer_data', { p_employer_id: id });
    if (error) { alert(error.message); return; }
    setEmployers(prev => prev.filter(e => e.id !== id));
    try {
      await deleteAccountLogin(id);
    } catch (err) {
      alert(t('admin.dashboard.shared.deleteLoginFailedError', { error: err.message }));
    }
  };

  // Same shape as handleDeleteEmployer: one SECURITY DEFINER RPC does the whole server-side
  // cascade (union_staff, apprenticeships, exams, portfolio, invites, conversations, the
  // linked directory profile), then the auth login goes through /api/admin-delete-account.
  const handleDeleteUnion = async (id) => {
    if (!window.confirm(t('admin.dashboard.unions.deleteConfirm'))) return;
    const { error } = await supabase.rpc('admin_delete_union_data', { p_union_id: id });
    if (error) { alert(error.message); return; }
    setUnions(prev => prev.filter(u => u.id !== id));
    try {
      await deleteAccountLogin(id);
    } catch (err) {
      alert(t('admin.dashboard.shared.deleteLoginFailedError', { error: err.message }));
    }
  };

  const loadAddonsLimits = async (employerId) => {
    const { data } = await supabase.rpc('employer_plan_usage', { p_employer_id: employerId });
    if (data) setAddonsLimits(prev => ({ ...prev, [employerId]: { job_limit: data.job_limit, user_limit: data.user_limit } }));
  };

  const toggleAddonsRow = (employer) => {
    if (expandedAddonsId === employer.id) {
      setExpandedAddonsId(null);
      return;
    }
    setExpandedAddonsId(employer.id);
    setAddonsEdits(prev => ({ ...prev, [employer.id]: { extra_jobs: employer.extraJobs, extra_users: employer.extraUsers } }));
    setAddonsStatus(prev => ({ ...prev, [employer.id]: null }));
    if (!addonsLimits[employer.id]) loadAddonsLimits(employer.id);
    // The Add-ons column sits past several others in this wide table, so on mobile a real tap
    // (unlike a test click, which auto-scrolls elements into view) leaves horizontal scroll
    // wherever the admin swiped to find the badge -- the expanded panel would render off to the
    // left of that, out of view. Snap back to the start so it's immediately visible.
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = 0;
  };

  // organization_staff carries no display name, so identity comes from the accepted invite
  // that created each seat (organization_invites.accepted_by -> email). A seat with no matching
  // accepted invite still lists, falling back to a short user id rather than being dropped --
  // an unnamed row the admin can still reassign beats a silently incomplete roster.
  const loadOrgStaff = async (organizationId) => {
    setOrgStaffLoading(prev => ({ ...prev, [organizationId]: true }));
    const [{ data: staff }, { data: invites }] = await Promise.all([
      supabase.from('organization_staff').select('user_id, role, created_at').eq('organization_id', organizationId).order('created_at', { ascending: true }),
      supabase.from('organization_invites').select('accepted_by, email').eq('organization_id', organizationId).not('accepted_by', 'is', null),
    ]);
    const emailByUser = Object.fromEntries((invites || []).map(i => [i.accepted_by, i.email]));
    setOrgStaffRows(prev => ({
      ...prev,
      [organizationId]: (staff || []).map(s => ({ ...s, email: emailByUser[s.user_id] || null })),
    }));
    setOrgStaffLoading(prev => ({ ...prev, [organizationId]: false }));
  };

  const toggleOrgStaffRow = (org) => {
    if (expandedOrgStaffId === org.id) { setExpandedOrgStaffId(null); return; }
    setExpandedOrgStaffId(org.id);
    setOrgReassignPick(prev => ({ ...prev, [org.id]: '' }));
    setOrgReassignStatus(prev => ({ ...prev, [org.id]: null }));
    loadOrgStaff(org.id);
    // Same horizontal-scroll snap-back as the Employers add-ons row: the Staff column sits
    // several columns in, so on a real mobile swipe the panel would otherwise open off-screen.
    if (tableScrollRef.current) tableScrollRef.current.scrollLeft = 0;
  };

  const handleReassignOrgAdmin = async (org) => {
    const newAdminUserId = orgReassignPick[org.id];
    if (!newAdminUserId) return;
    const rows = orgStaffRows[org.id] || [];
    const target = rows.find(r => r.user_id === newAdminUserId);
    const current = rows.find(r => r.role === 'admin');
    // Reassignment demotes a real person and promotes another across the whole organization --
    // name both sides in the confirm so the admin sees exactly what they're swapping.
    const confirmed = window.confirm(t('admin.dashboard.organizations.reassignAdminConfirm', {
      org: org.org_name || '—',
      from: current ? (current.email || current.user_id.slice(0, 8)) : '—',
      to: target ? (target.email || target.user_id.slice(0, 8)) : '—',
    }));
    if (!confirmed) return;

    setOrgReassignSaving(prev => ({ ...prev, [org.id]: true }));
    setOrgReassignStatus(prev => ({ ...prev, [org.id]: null }));
    const { error } = await supabase.rpc('reassign_organization_admin', {
      p_organization_id: org.id,
      p_new_admin_user_id: newAdminUserId,
    });
    setOrgReassignSaving(prev => ({ ...prev, [org.id]: false }));
    if (error) {
      setOrgReassignStatus(prev => ({ ...prev, [org.id]: { ok: false, msg: error.message } }));
      return;
    }
    setOrgReassignStatus(prev => ({ ...prev, [org.id]: { ok: true, msg: t('admin.dashboard.organizations.reassignAdminSuccess') } }));
    setOrgReassignPick(prev => ({ ...prev, [org.id]: '' }));
    // Re-read rather than patching local state: the RPC demotes the old admin and promotes the
    // new one in one transaction, so the server is the only place that knows the final roles.
    await loadOrgStaff(org.id);
  };

  const handleSaveAddons = async (employerId) => {
    const edit = addonsEdits[employerId] || { extra_jobs: 0, extra_users: 0 };
    const extraJobs  = parseInt(edit.extra_jobs, 10);
    const extraUsers = parseInt(edit.extra_users, 10);
    if (!Number.isInteger(extraJobs) || extraJobs < 0 || !Number.isInteger(extraUsers) || extraUsers < 0) {
      setAddonsStatus(prev => ({ ...prev, [employerId]: { ok: false, msg: t('admin.dashboard.employers.addonsInvalidError') } }));
      return;
    }
    setAddonsSaving(prev => ({ ...prev, [employerId]: true }));
    setAddonsStatus(prev => ({ ...prev, [employerId]: null }));

    const { error } = await supabase
      .from('employer_addons')
      .upsert({ employer_id: employerId, extra_jobs: extraJobs, extra_users: extraUsers }, { onConflict: 'employer_id' });

    setAddonsSaving(prev => ({ ...prev, [employerId]: false }));
    if (error) {
      setAddonsStatus(prev => ({ ...prev, [employerId]: { ok: false, msg: t('admin.dashboard.employers.addonsErrorSaving') } }));
      return;
    }

    setEmployers(prev => prev.map(e => (e.id === employerId ? { ...e, extraJobs, extraUsers } : e)));
    await loadAddonsLimits(employerId);
    setAddonsStatus(prev => ({ ...prev, [employerId]: { ok: true, msg: t('admin.dashboard.employers.addonsSavedLabel') } }));
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm(t('employer.dashboard.home.deleteJobConfirm'))) return;
    const { error } = await supabase.from('jobs').delete().eq('id', id);
    if (!error) setJobs(prev => prev.filter(j => j.id !== id));
  };

  const handleDeleteOrganization = async (id) => {
    if (!window.confirm(t('admin.dashboard.organizations.deleteConfirm'))) return;
    await supabase.from('organization_members').delete().eq('organization_id', id);
    await supabase.from('organization_staff').delete().eq('organization_id', id);
    await supabase.from('organization_invites').delete().eq('organization_id', id);
    await supabase.from('conversations').delete().eq('organization_id', id);
    await supabase.from('support_conversations').delete().eq('account_type', 'organization').eq('account_id', id);
    const { error } = await supabase.from('organizations').delete().eq('id', id);
    if (!error) setOrganizations(prev => prev.filter(o => o.id !== id));
  };

  const handleVerifyCert = async (id) => {
    const now = new Date().toISOString();
    const { error } = await supabase.from('worker_certifications').update({ verification_status: 'verified', verified_at: now, verified_by: adminUser?.id }).eq('id', id);
    if (error) { alert(`${t('admin.dashboard.certifications.verifyFailedErrorPrefix')}${error.message}`); return; }
    setCertifications(prev => prev.map(c => c.id === id ? { ...c, verification_status: 'verified', verified_at: now, verified_by: adminUser?.id } : c));
    const cert = certifications.find(c => c.id === id);
    if (cert) {
      supabase.from('notifications').insert({
        user_id:   cert.worker_id,
        user_type: 'worker',
        type:      'cert_verified',
        message:   `Your ${cert.certification_name} certification has been verified! ✅`,
        link_to:   JSON.stringify({ tab: 'Experience' }),
      }).then(({ error }) => {
        // The cert itself is already verified at this point -- a failure here must be visible
        // to the admin (not just console.error), since otherwise it looks identical to "nothing
        // went wrong" and the worker silently never finds out.
        if (error) {
          console.error('[notify] cert_verified insert:', error);
          alert(`${t('admin.dashboard.certifications.verifyNotifyFailedPrefix')}${error.message}`);
        }
      });
      sendNotificationEmail(supabase, {
        recipientId:   cert.worker_id,
        recipientType: 'worker',
        type:          'cert_verified',
        cert_name:     cert.certification_name,
      });
    } else {
      // The verification itself already succeeded (the UPDATE above completed) -- this only
      // means the local certifications list didn't have this row, so the worker never gets
      // notified. Log it loudly rather than silently skipping the whole notification block.
      console.error('[notify] cert_verified: cert', id, 'not found in local state, notification skipped');
    }
  };

  const handleRejectCert = async (id) => {
    const { error } = await supabase.from('worker_certifications').update({ verification_status: 'rejected' }).eq('id', id);
    if (error) { alert(`${t('admin.dashboard.certifications.rejectFailedErrorPrefix')}${error.message}`); return; }
    setCertifications(prev => prev.map(c => c.id === id ? { ...c, verification_status: 'rejected' } : c));
    const cert = certifications.find(c => c.id === id);
    if (cert) {
      supabase.from('notifications').insert({
        user_id:   cert.worker_id,
        user_type: 'worker',
        type:      'cert_rejected',
        message:   `Your ${cert.certification_name} certification could not be verified. Please upload a clearer document.`,
        link_to:   JSON.stringify({ tab: 'Experience' }),
      }).then(({ error }) => {
        if (error) {
          console.error('[notify] cert_rejected insert:', error);
          alert(`${t('admin.dashboard.certifications.rejectNotifyFailedPrefix')}${error.message}`);
        }
      });
      sendNotificationEmail(supabase, {
        recipientId:   cert.worker_id,
        recipientType: 'worker',
        type:          'cert_rejected',
        cert_name:     cert.certification_name,
      });
    } else {
      console.error('[notify] cert_rejected: cert', id, 'not found in local state, notification skipped');
    }
  };

  const setFf = (k, v) => setFoundingForm(p => ({ ...p, [k]: v }));

  const handleCreateFoundingInvite = async () => {
    if (!foundingForm.name.trim() || !foundingForm.email.trim()) {
      setFoundingMsg(t('admin.dashboard.organizations.contactRequiredError'));
      setFoundingMsgIsSuccess(false);
      return;
    }
    setFoundingMsg('');
    setFoundingSubmitting(true);

    const code = `founding-${crypto.randomUUID()}`;
    const { data: newInvite, error: insertError } = await supabase
      .from('organization_invites')
      .insert({
        organization_id: null,
        invited_by: adminUser.id,
        invite_type: 'founding',
        invite_code: code,
        email: foundingForm.email.trim(),
        status: 'pending',
      })
      .select('*')
      .single();

    if (insertError) {
      setFoundingMsg(insertError.message);
      setFoundingMsgIsSuccess(false);
      setFoundingSubmitting(false);
      return;
    }

    // Best-effort, matching the rest of the app's notification pattern -- a failed send
    // shouldn't undo an otherwise-successful invite; the DB row is the source of truth.
    try {
      await fetch('/api/notify-interest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'org_invite',
          recipient_email: foundingForm.email.trim(),
          recipient_name: foundingForm.name.trim(),
          invite_type: 'founding',
          invite_note: foundingForm.notes.trim(),
          invite_link: `${window.location.origin}/organization/join?code=${code}`,
        }),
      });
    } catch (_) {}

    setOrgInvites(prev => [{ ...newInvite, orgName: null }, ...prev]);
    setFoundingForm({ name: '', email: '', notes: '' });
    setFoundingSubmitting(false);
    setFoundingMsg(t('admin.dashboard.organizations.inviteSentMsg'));
    setFoundingMsgIsSuccess(true);
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  // Live search, one derived list per tab -- plain client-side filter, same pattern as
  // the job search on WorkerDashboard (no debounce, filters directly on every keystroke).
  const filteredWorkers = workerSearch.trim() ? workers.filter(w => {
    const q = workerSearch.toLowerCase();
    return `${w.first_name || ''} ${w.last_name || ''}`.toLowerCase().includes(q) || (w.email || '').toLowerCase().includes(q) || (w.trade || '').toLowerCase().includes(q);
  }) : workers;

  const filteredUnions = unionSearch.trim() ? unions.filter(u => {
    const q = unionSearch.toLowerCase();
    return (u.union_name || '').toLowerCase().includes(q)
      || (u.local_number || '').toLowerCase().includes(q)
      || (u.trade || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q);
  }) : unions;

  const filteredEmployers = employerSearch.trim() ? employers.filter(e => {
    const q = employerSearch.toLowerCase();
    return (e.company_name || '').toLowerCase().includes(q) || (e.full_name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
  }) : employers;

  const filteredJobs = jobSearch.trim() ? jobs.filter(j => {
    const q = jobSearch.toLowerCase();
    return (j.job_title || '').toLowerCase().includes(q) || (j.company_name || '').toLowerCase().includes(q) || (j.city || '').toLowerCase().includes(q);
  }) : jobs;

  const filteredApplications = applicationSearch.trim() ? applications.filter(a => {
    const q = applicationSearch.toLowerCase();
    return (a.workerName || '').toLowerCase().includes(q) || (a.jobTitle || '').toLowerCase().includes(q) || (a.companyName || '').toLowerCase().includes(q);
  }) : applications;

  const filteredWaitlist = waitlistSearch.trim() ? waitlist.filter(w => {
    const q = waitlistSearch.toLowerCase();
    return (w.full_name || '').toLowerCase().includes(q) || (w.email || '').toLowerCase().includes(q) || (w.city || '').toLowerCase().includes(q);
  }) : waitlist;

  const filteredOrganizations = orgSearch.trim() ? organizations.filter(o => {
    const q = orgSearch.toLowerCase();
    return (o.org_name || '').toLowerCase().includes(q) || (o.city || '').toLowerCase().includes(q) || (o.state || '').toLowerCase().includes(q);
  }) : organizations;

  if (!authorized) return <div style={{ background: NAVY, height: '100dvh' }}></div>;

  if (loading) return (
    <div style={{ background: NAVY, height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: AMBER, fontFamily: FONT_BODY, fontSize: '15px' }}>{t('admin.dashboard.shared.loadingAdminData')}</p>
    </div>
  );

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh', overflowX: 'hidden' }}>

      {/* Navbar */}
      <nav style={{
        position: 'sticky', top: 0, left: 0, right: 0,
        backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', height: 56, zIndex: 100,
      }}>
        <div onClick={() => setRefreshKey(k => k + 1)} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={isMobile ? 28 : 32} />
          <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'rgba(245,166,35,0.6)', letterSpacing: '0.12em', textTransform: 'uppercase', marginLeft: '4px' }}>
            {t('nav.admin.badge')}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <LanguageToggle userId={adminUser?.id} />
          <button
            onClick={handleSignOut}
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)', fontSize: '0.82rem', fontWeight: 600, padding: '7px 16px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
          >
            {t('common.signOut')}
          </button>
        </div>
      </nav>

      <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>

        {/* Stat cards */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '140px' }}><StatCard label={t('admin.dashboard.shared.statTotalWorkers')}      value={workers.length}      Icon={IconUsers}     iconColor={BLUE} /></div>
          <div style={{ flex: 1, minWidth: '140px' }}><StatCard label={t('admin.dashboard.shared.statTotalEmployers')}    value={employers.length}    Icon={IconBuilding}  iconColor={AMBER} /></div>
          <div style={{ flex: 1, minWidth: '140px' }}><StatCard label={t('admin.dashboard.shared.statTotalJobPostings')} value={jobs.length}         Icon={IconBriefcase} iconColor={PURPLE} /></div>
          <div style={{ flex: 1, minWidth: '140px' }}><StatCard label={t('admin.dashboard.shared.statTotalApplications')} value={applications.length} Icon={IconCheck}     iconColor={GREEN} /></div>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '0', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {TABS.map(tabName => {
            const count = tabName === 'Workers' ? workers.length : tabName === 'Employers' ? employers.length : tabName === 'Unions' ? unions.length : tabName === 'Jobs' ? jobs.length : tabName === 'Certifications' ? certifications.length : tabName === 'Waitlist' ? waitlist.length : tabName === 'Organizations' ? orgInvites.length : tabName === 'Support' ? supportUnreadCount : tabName === 'Settings' ? null : applications.length;
            return (
              <button
                key={tabName}
                onClick={() => setTab(tabName)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '10px 20px', fontSize: '0.875rem', fontWeight: 600,
                  fontFamily: FONT_BODY, whiteSpace: 'nowrap', flexShrink: 0,
                  color: tab === tabName ? AMBER : 'rgba(255,255,255,0.4)',
                  borderBottom: tab === tabName ? `2px solid ${AMBER}` : '2px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                {tabLabel(tabName)}
                {count !== null && (
                  <span style={{ marginLeft: '7px', fontSize: '0.7rem', fontWeight: 700, opacity: 0.6 }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Table container */}
        <div style={{ backgroundColor: NAVY_MID, borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
          <div ref={tableScrollRef} style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%', display: 'block' }}>

            {/* Workers */}
            {tab === 'Workers' && (
              <>
                <SearchBar value={workerSearch} onChange={setWorkerSearch} placeholder={t('admin.dashboard.workers.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />
                {workers.length === 0 ? <EmptyState text={t('admin.dashboard.workers.emptyNone')} /> : filteredWorkers.length === 0 ? <EmptyState text={t('admin.dashboard.workers.emptyNoMatch')} /> : (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t('admin.dashboard.workers.colName')}</th>
                        <th style={th}>{t('admin.dashboard.shared.email')}</th>
                        <th style={th}>{t('admin.dashboard.workers.colTrade')}</th>
                        <th style={th}>{t('admin.dashboard.workers.colZip')}</th>
                        <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.shared.dateJoined')}</th>
                        <th style={{ ...th, textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredWorkers.map(w => (
                        <tr key={w.id}>
                          <td style={{ ...td, fontWeight: 600 }}>{w.first_name} {w.last_name}</td>
                          <td style={tdMuted}>{w.email || '—'}</td>
                          <td style={tdMuted}>{w.trade}</td>
                          <td style={tdMuted}>{w.zip_code || '—'}</td>
                          <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(w.created_at)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <DemoToggleBtn isDemo={!!w.is_demo} onClick={() => handleToggleDemo('workers', w.id, w.is_demo)} t={t} />
                              <DeleteBtn onClick={() => handleDeleteWorker(w.id)} t={t} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {/* Employers */}
            {tab === 'Employers' && (
              <>
                <SearchBar value={employerSearch} onChange={setEmployerSearch} placeholder={t('admin.dashboard.employers.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />
                {employers.length === 0 ? <EmptyState text={t('admin.dashboard.employers.emptyNone')} /> : filteredEmployers.length === 0 ? <EmptyState text={t('admin.dashboard.employers.emptyNoMatch')} /> : (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t('admin.dashboard.employers.colCompanyName')}</th>
                        <th style={th}>{t('admin.dashboard.employers.colContactName')}</th>
                        <th style={th}>{t('admin.dashboard.shared.email')}</th>
                        <th style={th}>{t('admin.dashboard.shared.location')}</th>
                        <th style={th}>{t('admin.dashboard.employers.colJobsPosted')}</th>
                        <th style={th}>{t('admin.dashboard.employers.colAddons')}</th>
                        <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.shared.dateJoined')}</th>
                        <th style={{ ...th, textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredEmployers.map(e => {
                        const isExpanded = expandedAddonsId === e.id;
                        const edit = addonsEdits[e.id] || { extra_jobs: e.extraJobs, extra_users: e.extraUsers };
                        const limits = addonsLimits[e.id];
                        const saving = addonsSaving[e.id];
                        const status = addonsStatus[e.id];
                        return (
                          <Fragment key={e.id}>
                          <tr>
                            <td style={{ ...td, fontWeight: 600 }}>{e.company_name || '—'}</td>
                            <td style={tdMuted}>{e.full_name || '—'}</td>
                            <td style={tdMuted}>{e.email || '—'}</td>
                            <td style={tdMuted}>{e.city && e.state ? `${e.city}, ${e.state}` : '—'}</td>
                            <td style={{ ...td, color: e.jobsPosted > 0 ? AMBER : 'rgba(255,255,255,0.28)', fontWeight: e.jobsPosted > 0 ? 700 : 400 }}>
                              {e.jobsPosted}
                            </td>
                            <td style={td}>
                              <span
                                onClick={() => toggleAddonsRow(e)}
                                style={{
                                  fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer', padding: '4px 10px', borderRadius: '999px',
                                  backgroundColor: isExpanded ? AMBER : (e.extraJobs || e.extraUsers) ? 'rgba(245,166,35,0.12)' : 'rgba(255,255,255,0.06)',
                                  color: isExpanded ? NAVY : (e.extraJobs || e.extraUsers) ? AMBER : 'rgba(255,255,255,0.4)',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t('admin.dashboard.employers.addonsBadge', { jobs: e.extraJobs, users: e.extraUsers })}
                              </span>
                            </td>
                            <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(e.created_at)}</td>
                            <td style={{ ...td, textAlign: 'right' }}>
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                <DemoToggleBtn isDemo={!!e.is_demo} onClick={() => handleToggleDemo('employers', e.id, e.is_demo)} t={t} />
                                <DeleteBtn onClick={() => handleDeleteEmployer(e.id)} t={t} />
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={8} style={{ ...td, backgroundColor: '#0b1621', whiteSpace: 'normal' }}>
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', padding: '10px 4px' }}>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: '5px' }}>
                                      {t('admin.dashboard.employers.extraJobsLabel')}
                                    </label>
                                    <input
                                      type="number" min={0} value={edit.extra_jobs}
                                      onChange={ev => setAddonsEdits(prev => ({ ...prev, [e.id]: { ...edit, extra_jobs: ev.target.value } }))}
                                      style={{ width: '90px', padding: '8px 10px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY }}
                                    />
                                    <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.4)', marginTop: '5px' }}>
                                      {limits ? t('admin.dashboard.employers.currentJobLimit', { limit: limits.job_limit }) : '—'}
                                    </p>
                                  </div>
                                  <div>
                                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', marginBottom: '5px' }}>
                                      {t('admin.dashboard.employers.extraUsersLabel')}
                                    </label>
                                    <input
                                      type="number" min={0} value={edit.extra_users}
                                      onChange={ev => setAddonsEdits(prev => ({ ...prev, [e.id]: { ...edit, extra_users: ev.target.value } }))}
                                      style={{ width: '90px', padding: '8px 10px', fontSize: '0.88rem', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY }}
                                    />
                                    <p style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.4)', marginTop: '5px' }}>
                                      {limits ? t('admin.dashboard.employers.currentUserLimit', { limit: limits.user_limit }) : '—'}
                                    </p>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    <button
                                      onClick={() => handleSaveAddons(e.id)}
                                      disabled={saving}
                                      style={{
                                        background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem',
                                        padding: '9px 18px', borderRadius: '8px', fontFamily: FONT_BODY, alignSelf: 'flex-end',
                                        cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1,
                                      }}
                                    >
                                      {saving ? t('admin.dashboard.settings.savingButton') : t('admin.dashboard.settings.saveButton')}
                                    </button>
                                    {status && (
                                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: status.ok ? GREEN : '#F87171' }}>{status.msg}</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {/* Unions */}
            {tab === 'Unions' && (
              <>
                <SearchBar value={unionSearch} onChange={setUnionSearch} placeholder={t('admin.dashboard.unions.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />
                {unions.length === 0 ? <EmptyState text={t('admin.dashboard.unions.emptyNone')} /> : filteredUnions.length === 0 ? <EmptyState text={t('admin.dashboard.unions.emptyNoMatch')} /> : (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t('admin.dashboard.unions.colUnionName')}</th>
                        <th style={th}>{t('admin.dashboard.unions.colLocal')}</th>
                        <th style={th}>{t('admin.dashboard.unions.colTrade')}</th>
                        <th style={th}>{t('admin.dashboard.shared.email')}</th>
                        <th style={th}>{t('admin.dashboard.shared.location')}</th>
                        <th style={th}>{t('admin.dashboard.unions.colPostings')}</th>
                        <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.shared.dateJoined')}</th>
                        <th style={{ ...th, textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUnions.map(u => (
                        <tr key={u.id} data-union-row={u.id}>
                          <td style={{ ...td, fontWeight: 600 }}>{u.union_name || '—'}</td>
                          <td style={tdMuted}>{u.local_number || '—'}</td>
                          <td style={tdMuted}>{u.trade || '—'}</td>
                          <td style={tdMuted}>{u.email || '—'}</td>
                          <td style={tdMuted}>{u.city && u.state ? `${u.city}, ${u.state}` : '—'}</td>
                          <td style={{ ...td, color: u.postings > 0 ? AMBER : 'rgba(255,255,255,0.28)', fontWeight: u.postings > 0 ? 700 : 400 }}>
                            {u.postings}
                          </td>
                          <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(u.created_at)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <DemoToggleBtn isDemo={!!u.is_demo} onClick={() => handleToggleDemo('union_accounts', u.id, u.is_demo)} t={t} />
                              <DeleteBtn onClick={() => handleDeleteUnion(u.id)} t={t} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {/* Jobs */}
            {tab === 'Jobs' && (
              <>
                <SearchBar value={jobSearch} onChange={setJobSearch} placeholder={t('admin.dashboard.jobs.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />
                {jobs.length === 0 ? <EmptyState text={t('admin.dashboard.jobs.emptyNone')} /> : filteredJobs.length === 0 ? <EmptyState text={t('admin.dashboard.jobs.emptyNoMatch')} /> : (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t('admin.dashboard.jobs.colJobTitle')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colCompany')}</th>
                        <th style={th}>{t('admin.dashboard.shared.location')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colPayRate')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colStatus')}</th>
                        <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.jobs.colDatePosted')}</th>
                        <th style={{ ...th, textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredJobs.map(j => (
                        <tr key={j.id}>
                          <td style={{ ...td, fontWeight: 600 }}>{j.job_title || '—'}</td>
                          <td style={tdMuted}>{j.company_name || '—'}</td>
                          <td style={tdMuted}>{j.city && j.state ? `${j.city}, ${j.state}` : '—'}</td>
                          <td style={{ ...td, color: AMBER, fontWeight: 600 }}>
                            {j.salary_min && j.salary_max ? `$${j.salary_min}–$${j.salary_max}/hr` : '—'}
                          </td>
                          <td style={td}><StatusBadge status={j.status} t={t} /></td>
                          <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(j.created_at)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <DeleteBtn onClick={() => handleDeleteJob(j.id)} t={t} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {/* Applications */}
            {tab === 'Applications' && (
              <>
                <SearchBar value={applicationSearch} onChange={setApplicationSearch} placeholder={t('admin.dashboard.applications.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />
                {applications.length === 0 ? <EmptyState text={t('admin.dashboard.applications.emptyNone')} /> : filteredApplications.length === 0 ? <EmptyState text={t('admin.dashboard.applications.emptyNoMatch')} /> : (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t('admin.dashboard.applications.colWorkerName')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colJobTitle')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colCompany')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colStatus')}</th>
                        <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.applications.colDateApplied')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.map(app => (
                        <tr key={app.id}>
                          <td style={{ ...td, fontWeight: 600 }}>{app.workerName}</td>
                          <td style={tdMuted}>{app.jobTitle}</td>
                          <td style={tdMuted}>{app.companyName}</td>
                          <td style={td}><StatusBadge status={app.status || 'interested'} t={t} /></td>
                          <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(app.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}

            {/* Certifications */}
            {tab === 'Certifications' && (() => {
              const statusFiltered = certFilter === 'all' ? certifications
                : certFilter === 'pending'  ? certifications.filter(c => c.verification_status === 'pending')
                : certFilter === 'verified' ? certifications.filter(c => c.verification_status === 'verified')
                : certifications.filter(c => c.verification_status === 'rejected');
              const filtered = certSearch.trim() ? statusFiltered.filter(c => {
                const q = certSearch.toLowerCase();
                return (c.workerName || '').toLowerCase().includes(q) || (c.certification_name || '').toLowerCase().includes(q) || (c.issuing_organization || '').toLowerCase().includes(q);
              }) : statusFiltered;
              return (
                <>
                  {/* Filter tabs */}
                  <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid rgba(255,255,255,0.07)', backgroundColor: '#0b1621' }}>
                    {[
                      { key: 'all',      labelKey: 'admin.dashboard.certifications.filterAll',            count: certifications.length },
                      { key: 'pending',  labelKey: 'admin.dashboard.certifications.filterPendingReview', count: certifications.filter(c => c.verification_status === 'pending').length },
                      { key: 'verified', labelKey: 'admin.dashboard.certifications.filterVerified',       count: certifications.filter(c => c.verification_status === 'verified').length },
                      { key: 'rejected', labelKey: 'admin.dashboard.certifications.filterRejected',       count: certifications.filter(c => c.verification_status === 'rejected').length },
                    ].map(f => (
                      <button
                        key={f.key}
                        onClick={() => setCertFilter(f.key)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: '10px 18px', fontSize: '0.82rem', fontWeight: 600,
                          fontFamily: FONT_BODY, whiteSpace: 'nowrap',
                          color: certFilter === f.key ? AMBER : 'rgba(255,255,255,0.4)',
                          borderBottom: certFilter === f.key ? `2px solid ${AMBER}` : '2px solid transparent',
                          marginBottom: '-1px',
                        }}
                      >
                        {t(f.labelKey)}
                        <span style={{ marginLeft: '6px', fontSize: '0.7rem', opacity: 0.6 }}>{f.count}</span>
                      </button>
                    ))}
                  </div>

                  <SearchBar value={certSearch} onChange={setCertSearch} placeholder={t('admin.dashboard.certifications.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />

                  {filtered.length === 0 ? <EmptyState text={certSearch.trim() ? t('admin.dashboard.certifications.emptyNoMatchSearch') : t('admin.dashboard.certifications.emptyNoMatchFilter')} /> : (
                    <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                      <thead>
                        <tr>
                          <th style={th}>{t('admin.dashboard.certifications.colWorker')}</th>
                          <th style={th}>{t('admin.dashboard.certifications.colCertification')}</th>
                          <th style={th}>{t('admin.dashboard.certifications.colIssuingOrg')}</th>
                          <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.certifications.colExpiry')}</th>
                          <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.certifications.colDocument')}</th>
                          <th style={th}>{t('admin.dashboard.jobs.colStatus')}</th>
                          <th style={{ ...th, textAlign: 'right' }}>{t('admin.dashboard.certifications.colActions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map(cert => (
                          <tr key={cert.id}>
                            <td style={{ ...td, fontWeight: 600 }}>{cert.workerName}</td>
                            <td style={td}>{cert.certification_name || '—'}</td>
                            <td style={tdMuted}>{cert.issuing_organization || '—'}</td>
                            <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(cert.expiry_date)}</td>
                            <td style={{ ...td, display: isMobile ? 'none' : 'table-cell' }}>
                              {cert.document_url ? (
                                <a href={cert.document_url} target="_blank" rel="noopener noreferrer" style={{ color: AMBER, fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}>{t('admin.dashboard.certifications.viewDocLink')}</a>
                              ) : (
                                <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.82rem' }}>{t('admin.dashboard.certifications.noDocument')}</span>
                              )}
                            </td>
                            <td style={td}><CertStatusBadge status={cert.verification_status || 'unverified'} t={t} /></td>
                            <td style={{ ...td, textAlign: 'right' }}>
                              {(cert.verification_status === 'pending' || cert.verification_status === 'unverified' || !cert.verification_status) && (
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                  <button
                                    onClick={() => handleVerifyCert(cert.id)}
                                    style={{ background: 'rgba(29,158,117,0.15)', border: '1px solid rgba(29,158,117,0.4)', color: '#1D9E75', fontSize: '0.75rem', fontWeight: 700, padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY }}
                                  >{t('admin.dashboard.certifications.verifyButton')}</button>
                                  <button
                                    onClick={() => handleRejectCert(cert.id)}
                                    style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#F87171', fontSize: '0.75rem', fontWeight: 600, padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY }}
                                  >{t('admin.dashboard.certifications.rejectButton')}</button>
                                </div>
                              )}
                              {cert.verification_status === 'verified' && (
                                <span style={{ color: '#1D9E75', fontSize: '0.82rem', fontWeight: 700 }}>{t('admin.dashboard.certifications.verifiedCheck')}</span>
                              )}
                              {cert.verification_status === 'rejected' && (
                                <button
                                  onClick={() => handleVerifyCert(cert.id)}
                                  style={{ background: 'rgba(29,158,117,0.15)', border: '1px solid rgba(29,158,117,0.4)', color: '#1D9E75', fontSize: '0.75rem', fontWeight: 700, padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontFamily: FONT_BODY }}
                                >{t('admin.dashboard.certifications.reverifyButton')}</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              );
            })()}

            {/* Waitlist */}
            {tab === 'Waitlist' && (() => {
              const wlWorkers   = waitlist.filter(w => w.user_type === 'worker').length;
              const wlEmployers = waitlist.filter(w => w.user_type === 'employer').length;
              return (
                <>
                  <div style={{ padding: '14px 20px', backgroundColor: '#0b1621', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>
                      {t('admin.dashboard.waitlist.workersCountLabel')} <strong style={{ color: WHITE }}>{wlWorkers}</strong>
                    </span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>
                      {t('admin.dashboard.waitlist.employersCountLabel')} <strong style={{ color: AMBER }}>{wlEmployers}</strong>
                    </span>
                  </div>
                  <SearchBar value={waitlistSearch} onChange={setWaitlistSearch} placeholder={t('admin.dashboard.waitlist.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />
                  {waitlist.length === 0 ? <EmptyState text={t('admin.dashboard.waitlist.emptyNone')} /> : filteredWaitlist.length === 0 ? <EmptyState text={t('admin.dashboard.waitlist.emptyNoMatch')} /> : (
                    <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                      <thead>
                        <tr>
                          <th style={th}>{t('admin.dashboard.workers.colName')}</th>
                          <th style={th}>{t('admin.dashboard.shared.email')}</th>
                          <th style={th}>{t('admin.dashboard.waitlist.colType')}</th>
                          <th style={th}>{t('admin.dashboard.waitlist.colCity')}</th>
                          <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.workers.colZip')}</th>
                          <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.waitlist.colDateSignedUp')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredWaitlist.map(w => (
                          <tr key={w.id}>
                            <td style={{ ...td, fontWeight: 600 }}>{w.full_name || '—'}</td>
                            <td style={tdMuted}>{w.email || '—'}</td>
                            <td style={td}>
                              {w.user_type === 'employer' ? (
                                <span style={{ backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', whiteSpace: 'nowrap' }}>
                                  {t('admin.dashboard.waitlist.typeEmployer')}
                                </span>
                              ) : (
                                <span style={{ backgroundColor: 'rgba(13,27,42,0.6)', color: 'rgba(255,255,255,0.7)', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.12)' }}>
                                  {t('admin.dashboard.waitlist.typeWorker')}
                                </span>
                              )}
                            </td>
                            <td style={tdMuted}>{w.city || '—'}</td>
                            <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{w.zip || '—'}</td>
                            <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(w.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              );
            })()}

            {/* Organizations */}
            {tab === 'Organizations' && (
              <>
                <SearchBar value={orgSearch} onChange={setOrgSearch} placeholder={t('admin.dashboard.organizations.searchPlaceholder')} clearAriaLabel={t('admin.dashboard.shared.clearSearchAriaLabel')} />
                {organizations.length === 0 ? <EmptyState text={t('admin.dashboard.organizations.emptyNone')} /> : filteredOrganizations.length === 0 ? <EmptyState text={t('admin.dashboard.organizations.emptyNoMatch')} /> : (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t('admin.dashboard.organizations.colOrganization')}</th>
                        <th style={th}>{t('admin.dashboard.shared.location')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colStatus')}</th>
                        <th style={th}>{t('admin.dashboard.organizations.colStaff')}</th>
                        <th style={th}>{t('admin.dashboard.organizations.colMembers')}</th>
                        <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.organizations.colCreated')}</th>
                        <th style={{ ...th, textAlign: 'right' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrganizations.map(o => {
                        const isStaffExpanded = expandedOrgStaffId === o.id;
                        const staffRows = orgStaffRows[o.id] || [];
                        const reassignable = staffRows.filter(s => s.role !== 'admin');
                        const pick = orgReassignPick[o.id] || '';
                        const reassignStatus = orgReassignStatus[o.id];
                        return (
                        <Fragment key={o.id}>
                        <tr>
                          <td style={{ ...td, fontWeight: 600 }}>{o.org_name || '—'}</td>
                          <td style={tdMuted}>{o.city && o.state ? `${o.city}, ${o.state}` : '—'}</td>
                          <td style={td}><StatusBadge status={o.deactivated_at ? 'deactivated' : 'active'} t={t} /></td>
                          {/* The staff count doubles as the roster toggle, matching how the
                              Employers tab's add-ons badge opens its own panel. */}
                          <td style={td}>
                            <span
                              onClick={() => toggleOrgStaffRow(o)}
                              style={{
                                fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', padding: '4px 10px', borderRadius: '999px',
                                backgroundColor: isStaffExpanded ? AMBER : o.staffCount > 0 ? 'rgba(245,166,35,0.12)' : 'rgba(255,255,255,0.06)',
                                color: isStaffExpanded ? NAVY : o.staffCount > 0 ? AMBER : 'rgba(255,255,255,0.4)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {t('admin.dashboard.organizations.staffBadge', { count: o.staffCount })}
                            </span>
                          </td>
                          <td style={{ ...td, color: o.memberCount > 0 ? AMBER : 'rgba(255,255,255,0.28)', fontWeight: o.memberCount > 0 ? 700 : 400 }}>{o.memberCount}</td>
                          <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(o.created_at)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <DeleteBtn onClick={() => handleDeleteOrganization(o.id)} t={t} />
                          </td>
                        </tr>
                        {isStaffExpanded && (
                          <tr>
                            <td colSpan={7} style={{ ...td, backgroundColor: '#0b1621', whiteSpace: 'normal' }}>
                              {/* This cell inherits the admin table's full (scrollable) width --
                                  ~829px at a 390px viewport -- so an unconstrained panel puts the
                                  role badges off-screen until the admin scrolls right. Cap it to
                                  the viewport on mobile; toggleOrgStaffRow already resets
                                  scrollLeft so the panel opens where they're looking. */}
                              <div style={{ padding: '10px 4px', maxWidth: isMobile ? '330px' : '640px' }}>
                                <p style={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px' }}>
                                  {t('admin.dashboard.organizations.staffRosterHeading')}
                                </p>
                                {orgStaffLoading[o.id] ? (
                                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>{t('common.loading')}</p>
                                ) : staffRows.length === 0 ? (
                                  <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)' }}>{t('admin.dashboard.organizations.staffRosterEmpty')}</p>
                                ) : (
                                  <>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
                                      {staffRows.map(s => (
                                        <div key={s.user_id} style={{
                                          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                                          backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '8px 12px',
                                        }}>
                                          <span style={{ fontSize: '0.82rem', color: WHITE, flex: 1, minWidth: 0, wordBreak: 'break-all' }}>
                                            {s.email || s.user_id.slice(0, 8)}
                                          </span>
                                          <span style={{
                                            fontSize: '0.66rem', fontWeight: 700, borderRadius: '999px', padding: '2px 10px',
                                            textTransform: 'capitalize', flexShrink: 0,
                                            backgroundColor: s.role === 'admin' ? 'rgba(245,166,35,0.15)' : 'rgba(255,255,255,0.06)',
                                            color: s.role === 'admin' ? AMBER : 'rgba(255,255,255,0.5)',
                                          }}>
                                            {s.role}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                    {reassignable.length === 0 ? (
                                      <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5 }}>
                                        {t('admin.dashboard.organizations.reassignAdminNoTargets')}
                                      </p>
                                    ) : (
                                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                                        <select
                                          value={pick}
                                          onChange={ev => setOrgReassignPick(prev => ({ ...prev, [o.id]: ev.target.value }))}
                                          style={{
                                            padding: '9px 12px', fontSize: '0.84rem', borderRadius: '8px', color: WHITE, outline: 'none',
                                            border: '1.5px solid rgba(255,255,255,0.12)', backgroundColor: 'rgba(255,255,255,0.06)',
                                            fontFamily: FONT_BODY, maxWidth: '100%',
                                          }}
                                        >
                                          <option value="">{t('admin.dashboard.organizations.reassignAdminSelectPlaceholder')}</option>
                                          {reassignable.map(s => (
                                            <option key={s.user_id} value={s.user_id}>{s.email || s.user_id.slice(0, 8)}</option>
                                          ))}
                                        </select>
                                        <button
                                          onClick={() => handleReassignOrgAdmin(o)}
                                          disabled={!pick || orgReassignSaving[o.id]}
                                          style={{
                                            background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.82rem',
                                            padding: '9px 18px', borderRadius: '8px', fontFamily: FONT_BODY,
                                            cursor: (!pick || orgReassignSaving[o.id]) ? 'default' : 'pointer',
                                            opacity: (!pick || orgReassignSaving[o.id]) ? 0.5 : 1,
                                          }}
                                        >
                                          {orgReassignSaving[o.id] ? t('admin.dashboard.settings.savingButton') : t('admin.dashboard.organizations.reassignAdminButton')}
                                        </button>
                                        {reassignStatus && (
                                          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: reassignStatus.ok ? GREEN : '#F87171' }}>
                                            {reassignStatus.msg}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}

              <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '1rem', marginBottom: '1.75rem' }}>
                  <div style={{ backgroundColor: NAVY, borderRadius: '10px', padding: '18px 20px', flex: 1, maxWidth: isMobile ? 'none' : '420px' }}>
                    <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
                      {t('admin.dashboard.organizations.inviteHeading')}
                    </p>
                    <div style={fld}>
                      <label style={lbl}>{t('admin.dashboard.organizations.contactNameLabel')}</label>
                      <input style={inp} placeholder={t('employer.signup.step1.fullNamePlaceholder')} value={foundingForm.name} onChange={e => setFf('name', e.target.value)} />
                    </div>
                    <div style={fld}>
                      <label style={lbl}>{t('admin.dashboard.shared.email')}</label>
                      <input style={inp} type="email" placeholder="jane@example.com" value={foundingForm.email} onChange={e => setFf('email', e.target.value)} />
                    </div>
                    <div style={fld}>
                      <label style={lbl}>{t('admin.dashboard.organizations.notesLabel')} <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.3)' }}>{t('admin.dashboard.organizations.notesOptional')}</span></label>
                      <textarea
                        style={{ ...inp, resize: 'vertical', minHeight: '70px', lineHeight: 1.5 }}
                        placeholder={t('admin.dashboard.organizations.notesPlaceholder')}
                        value={foundingForm.notes}
                        onChange={e => setFf('notes', e.target.value)}
                      />
                    </div>
                    <button
                      onClick={handleCreateFoundingInvite}
                      disabled={foundingSubmitting}
                      style={{
                        width: '100%', backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px',
                        padding: '10px', fontWeight: 700, fontSize: '0.85rem', cursor: foundingSubmitting ? 'default' : 'pointer',
                        opacity: foundingSubmitting ? 0.7 : 1, fontFamily: FONT_BODY, marginTop: '4px',
                      }}
                    >
                      {foundingSubmitting ? t('admin.dashboard.organizations.sendingButton') : t('admin.dashboard.organizations.sendFoundingInviteButton')}
                    </button>
                    {foundingMsg && (
                      <p style={{ fontSize: '0.78rem', fontWeight: 600, color: foundingMsgIsSuccess ? '#1D9E75' : '#F87171', marginTop: '10px' }}>
                        {foundingMsg}
                      </p>
                    )}
                  </div>
                </div>

                <p style={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(255,255,255,0.32)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  {t('admin.dashboard.organizations.foundingInvitesSentHeading')}
                </p>

                {orgInvites.length === 0 ? (
                  <EmptyState text={t('admin.dashboard.organizations.emptyNoInvites')} />
                ) : (
                  <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={th}>{t('admin.dashboard.shared.email')}</th>
                        <th style={th}>{t('admin.dashboard.jobs.colStatus')}</th>
                        <th style={th}>{t('admin.dashboard.organizations.colOrganizationCol')}</th>
                        <th style={{ ...th, display: isMobile ? 'none' : 'table-cell' }}>{t('admin.dashboard.organizations.colDateSent')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgInvites.map(inv => (
                        <tr key={inv.id}>
                          <td style={{ ...td, fontWeight: 600 }}>{inv.email || '—'}</td>
                          <td style={td}><InviteStatusBadge status={inv.status} t={t} /></td>
                          <td style={tdMuted}>{inv.orgName || '—'}</td>
                          <td style={{ ...tdMuted, display: isMobile ? 'none' : 'table-cell' }}>{fmt(inv.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              </>
            )}

            {/* Support */}
            {tab === 'Support' && (
              <div style={{ height: 'calc(100dvh - 260px)', minHeight: '480px' }}>
                <AdminSupportMessages supabase={supabase} adminUser={adminUser} onUnreadCountChange={setSupportUnreadCount} />
              </div>
            )}

            {/* Settings */}
            {tab === 'Settings' && <AdminSettings supabase={supabase} />}

          </div>
        </div>

        {/* Legal links */}
        <div style={{ marginTop: '28px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          <LegalLinksRow fontSize="0.7rem" />
          <p style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)', fontFamily: FONT_BODY }}>{t('common.copyright')}</p>
        </div>
      </div>
    </div>
  );
}
